import { Button } from '@chakra-ui/react';
import { UserPlus } from 'iconoir-react';
import { lazy, Suspense, useCallback, useState } from 'react';
import type React from 'react';
import { useTranslation } from 'react-i18next';

// The invitation wizard is a heavy tree (every collab step, the editor) behind a
// CTA most visits never press. Lazy + mount-on-open keeps it out of the
// invitations bundle, and out of this module's import graph.
const SendInvitationModal = lazy(
  () => import('@/components/actions/send-invitation/send-invitation-modal')
);

interface InviteAffiliatesButtonProps {
  /**
   * Program the surrounding surface is scoped to. Preselects it on the modal's
   * program step. Omitted on the cross-program invitations page, where the
   * brand picks the program inside the modal.
   */
  programId?: string;
}

const ICON_SIZE = 16;

/**
 * Entry point into the existing invitation flow, opened with no target so the
 * modal starts on its recipient step (pick an existing affiliate or type an
 * email). No new flow: this only mounts `SendInvitationModal` in its
 * `smart-input` entry mode.
 */
const InviteAffiliatesButton: React.FC<InviteAffiliatesButtonProps> = ({ programId }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const handleOpen = useCallback(() => setIsOpen(true), []);
  const handleClose = useCallback(() => setIsOpen(false), []);

  return (
    <>
      <Button colorPalette="primary" onClick={handleOpen}>
        <UserPlus width={ICON_SIZE} height={ICON_SIZE} />
        {t('invitations.inviteAffiliates')}
      </Button>
      {isOpen && (
        <Suspense fallback={null}>
          <SendInvitationModal isOpen onClose={handleClose} preselectedProgramId={programId} />
        </Suspense>
      )}
    </>
  );
};

export default InviteAffiliatesButton;
